"use client"

import { AuthFormHeading } from "./auth-form-heading"
import { KayitOnayAlanlari } from "./kayit-onay-alanlari"

import { zodResolver } from "@hookform/resolvers/zod"
import { useForm } from "react-hook-form"
import { z } from "zod"

import {
  KULLANIM_KOSULLARI,
  KULLANIM_KOSULLARI_VERSIYON,
  KVKK_AYDINLATMA_METNI,
  KVKK_AYDINLATMA_VERSIYON,
  YasalBelgeDialog,
} from "@/features/yasal"
import { LoadingButton } from "@/shared/components/feedback/loading-button"
import { FieldRadioGroup, FieldText } from "@/shared/components/form/form-fields"
import { Form } from "@/shared/components/ui/form"
import useDialogState from "@/shared/hooks/use-dialog-state"

import type { KayitHesapTipi, KayitTalebiRequest } from "../types/kayit-types"

const formSchema = z.object({
  ad_soyad: z.string().min(2, "Ad soyad / ünvan zorunludur"),
  email: z.string().email("Geçersiz e-posta"),
  telefon: z.string().min(10, "Geçerli bir telefon numarası girin"),
  hesap_tipi: z.enum(["MUSAVIR", "SIRKET"]),
  kullanim_kosullari: z.boolean().refine(Boolean, "Devam etmek için Kullanım Koşulları'nı kabul edin"),
  kvkk_aydinlatma: z.boolean().refine(Boolean, "Devam etmek için Aydınlatma Metni'ni okuduğunuzu onaylayın"),
  ticari_ileti_onayi: z.boolean(),
})

type FormValues = z.infer<typeof formSchema>

const hesapTipiOptions = [
  { label: "Mali Müşavir", value: "MUSAVIR" satisfies KayitHesapTipi },
  { label: "Şirket", value: "SIRKET" satisfies KayitHesapTipi },
]

interface KayitFormProps {
  isSubmitting: boolean
  hataMesaji: string | null
  onSubmit: (values: KayitTalebiRequest) => void
  onDegisiklik: () => void
}

export function KayitForm({
  isSubmitting,
  hataMesaji,
  onSubmit,
  onDegisiklik,
}: KayitFormProps) {
  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      ad_soyad: "",
      email: "",
      telefon: "",
      hesap_tipi: "MUSAVIR",
      kullanim_kosullari: false,
      kvkk_aydinlatma: false,
      ticari_ileti_onayi: false,
    },
  })
  const [belge, setBelge] = useDialogState<"aydinlatma" | "kosullar">()

  // Onay kaydı hangi metin sürümünün okunduğunu / kabul edildiğini taşır; kutucukların
  // kendisi gönderilmez (işaretsiz form zod'dan geçmez).
  const gonder = (values: FormValues) =>
    onSubmit({
      ad_soyad: values.ad_soyad,
      email: values.email,
      telefon: values.telefon,
      hesap_tipi: values.hesap_tipi,
      ticari_ileti_onayi: values.ticari_ileti_onayi,
      kvkk_aydinlatma_versiyon: KVKK_AYDINLATMA_VERSIYON,
      kullanim_kosullari_versiyon: KULLANIM_KOSULLARI_VERSIYON,
    })

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(gonder)}
        onChange={onDegisiklik}
        className="space-y-6"
      >
        <AuthFormHeading title="Ücretsiz hesap oluşturun">
            14 gün boyunca tüm özellikleri deneyin. Kredi kartı gerekmez.
          </AuthFormHeading>

        <FieldText name="ad_soyad" label="Ad Soyad / Ünvan" autoComplete="organization" />
        <FieldText name="email" label="E-posta" type="email" autoComplete="email" />
        <FieldText name="telefon" label="Telefon" type="tel" autoComplete="tel" />

        <FieldRadioGroup
          name="hesap_tipi"
          label="Hesap Tipi"
          options={hesapTipiOptions}
          description="Paket aileniz bu seçime göre belirlenir ve sonradan değiştirilemez."
        />

        <KayitOnayAlanlari
          onAydinlatmaAc={() => setBelge("aydinlatma")}
          onKosullarAc={() => setBelge("kosullar")}
        />

        {hataMesaji && <p role="alert" className="text-sm text-destructive">{hataMesaji}</p>}

        <LoadingButton type="submit" className="w-full" loading={isSubmitting}>
          Doğrulama kodu gönder
        </LoadingButton>

        <p className="text-center text-xs text-muted-foreground">
          Doğrulama kodu e-posta adresinize gönderilir; SMS gönderilmez.
        </p>
      </form>

      <YasalBelgeDialog
        belge={KVKK_AYDINLATMA_METNI}
        open={belge === "aydinlatma"}
        onOpenChange={(open) => setBelge(open ? "aydinlatma" : null)}
      />
      <YasalBelgeDialog
        belge={KULLANIM_KOSULLARI}
        open={belge === "kosullar"}
        onOpenChange={(open) => setBelge(open ? "kosullar" : null)}
      />
    </Form>
  )
}
